import { useState } from 'react'
import { router } from '@inertiajs/react'
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { StageBadge } from '@/components/shared/StageBadge'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { FrequencyBadge } from '@/components/shared/FrequencyBadge'
import { PaymentSummary } from '@/components/shared/PaymentSummary'
import { DocumentSection } from '@/components/shared/DocumentSection'
import { CalendarEventsSection } from '@/components/shared/CalendarEventsSection'
import { DetailGrid } from '@/components/backoffice/DetailGrid'
import { BackofficeComplianceFilingDetailed, BackofficeComplianceFilingRecordDetailed } from '@/types'

type Props = {
  filing: BackofficeComplianceFilingDetailed
}

function RecordDetail({ record, filing }: { record: BackofficeComplianceFilingRecordDetailed; filing: BackofficeComplianceFilingDetailed }) {
  const [expanded, setExpanded] = useState(false)
  const [showRejectForm, setShowRejectForm] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const confirm = useConfirm()

  const handleAccept = async () => {
    if (!await confirm({ title: 'Accept Filing?', description: 'This will mark the filing as accepted.' })) return
    router.post(`/backoffice/compliance_filings/${filing.id}/records/${record.id}/filing/acceptance`)
  }

  const handleRejectConfirm = () => {
    if (!rejectionReason.trim()) return
    router.post(`/backoffice/compliance_filings/${filing.id}/records/${record.id}/filing/rejection`, { rejection: { reason: rejectionReason.trim() } })
    setShowRejectForm(false)
    setRejectionReason('')
  }

  const handleComplete = async () => {
    if (!await confirm({ title: 'Mark as Complete?', description: 'Mark this filing period as complete?' })) return
    router.post(`/backoffice/compliance_filings/${filing.id}/records/${record.id}/completion`)
  }

  return (
    <div className="border rounded-lg">
      {/* Clickable header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          <span className="text-sm font-medium">
            {new Date(record.period_start_date).toLocaleDateString()} - {new Date(record.period_end_date).toLocaleDateString()}
          </span>
          <StageBadge stage={record.stage} variant="compliance" />
          {record.overdue && (
            <Badge className="bg-red-600 text-white font-normal text-xs flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />Overdue
            </Badge>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          Due: {new Date(record.due_date).toLocaleDateString()}
        </span>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t px-4 py-4 space-y-4">
          {/* Payment */}
          <PaymentSummary
            payment={record.payment}
            adminActions={record.payment ? {
              invoicePath: record.payment.invoice_path || undefined,
              confirmPaymentPath: record.payment.confirm_payment_path,
              refundPath: record.payment.refund_path,
            } : undefined}
          />

          {/* Filing Status */}
          {record.filing && (
            <section className="border rounded-lg px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Filing Status</p>
                <div className="flex items-center gap-2">
                  {record.filing.accepted && <Badge className="bg-green-600 text-white font-normal text-xs">Accepted</Badge>}
                  {record.filing.rejected && <Badge className="bg-red-600 text-white font-normal text-xs">Rejected</Badge>}
                  {record.filing.pending && <Badge className="bg-blue-600 text-white font-normal text-xs">Pending</Badge>}
                </div>
              </div>
              <div className="space-y-1.5">
                {record.filing.submitted_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Submitted</span>
                    <span>{new Date(record.filing.submitted_at).toLocaleDateString()}</span>
                  </div>
                )}
                {record.filing.reference_number && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Reference</span>
                    <span>{record.filing.reference_number}</span>
                  </div>
                )}
                {record.filing.rejection_reason && (
                  <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                    {record.filing.rejection_reason}
                  </div>
                )}
              </div>

              {/* Accept / Reject buttons */}
              {record.filing.pending && (
                <div className="mt-3 pt-2 border-t space-y-3">
                  {!showRejectForm ? (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleAccept} className="h-7 text-xs">Accept</Button>
                      <Button variant="outline" size="sm" onClick={() => setShowRejectForm(true)} className="h-7 text-xs text-red-600">Reject</Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Rejection Reason</p>
                      <textarea
                        className="w-full border rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                        rows={3}
                        placeholder="Enter rejection reason..."
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                      />
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={handleRejectConfirm} disabled={!rejectionReason.trim()} className="h-7 text-xs text-red-600">
                          Confirm Rejection
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setShowRejectForm(false); setRejectionReason('') }} className="h-7 text-xs">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Completion */}
          {!record.completion && record.filing?.accepted && (
            <section className="border rounded-lg px-4 py-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Mark as Complete</p>
                <Button size="sm" onClick={handleComplete} className="h-7 text-xs">Complete</Button>
              </div>
            </section>
          )}

          {record.completion && (
            <section className="border rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-600">Completed</span>
              </div>
              <div className="space-y-1 text-sm text-muted-foreground">
                {record.completion.completed_at && (
                  <p>Completed: {new Date(record.completion.completed_at).toLocaleDateString()}</p>
                )}
                {record.completion.completed_by && (
                  <p>By: {record.completion.completed_by}</p>
                )}
              </div>
            </section>
          )}

          {/* Documents */}
          <DocumentSection
            categories={[
              {
                label: 'Application Documents',
                documents: record.application_documents || [],
                uploadPath: `/backoffice/compliance_filings/${filing.id}/records/${record.id}/application_documents`,
                deletePath: `/backoffice/compliance_filings/${filing.id}/records/${record.id}/application_documents`,
              },
              {
                label: 'Filing Documents',
                documents: record.filing_documents || [],
                uploadPath: `/backoffice/compliance_filings/${filing.id}/records/${record.id}/filing_documents`,
                deletePath: `/backoffice/compliance_filings/${filing.id}/records/${record.id}/filing_documents`,
              },
              {
                label: 'Completion Documents',
                documents: record.completion_documents || [],
                uploadPath: `/backoffice/compliance_filings/${filing.id}/records/${record.id}/completion_documents`,
                deletePath: `/backoffice/compliance_filings/${filing.id}/records/${record.id}/completion_documents`,
              },
            ]}
          />

          {/* Calendar Events */}
          <CalendarEventsSection
            events={record.calendar_events || []}
            createPath={record.create_event_path || undefined}
          />
        </div>
      )}
    </div>
  )
}

export default function ComplianceFilingsShow({ filing }: Props) {
  return (
    <BackofficeLayout title={filing.type_name} breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Compliance Filings', href: '/backoffice/compliance_filings' }, { label: filing.type_name }]}>
      <div className="space-y-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{filing.type_name}</h1>
            <StatusBadge status={filing.status} />
            <FrequencyBadge frequency={filing.frequency} />
          </div>
          <p className="text-sm text-muted-foreground">{filing.business_name} · {filing.account_name} · {filing.jurisdiction_label || filing.type_code}</p>
        </div>

        <DetailGrid items={[
          { label: 'Start Date', value: new Date(filing.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) },
          {
            label: 'Next Due Date',
            value: filing.next_due_date
              ? <span className={filing.next_due_date && new Date(filing.next_due_date) < new Date() ? 'text-red-600 font-medium' : ''}>
                  {new Date(filing.next_due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              : '—',
          },
          { label: 'Frequency', value: filing.frequency.replace('_', '-') },
          { label: 'Jurisdiction', value: filing.jurisdiction_label || filing.type_code || '—' },
          { label: 'Status', value: <StatusBadge status={filing.status} /> },
        ]} />

        <div>
          <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Filing Records</p>
          {filing.records.length === 0 ? (
            <p className="text-sm text-muted-foreground">No filing records yet</p>
          ) : (
            <div className="space-y-3">
              {filing.records.map((record) => (
                <RecordDetail key={record.id} record={record} filing={filing} />
              ))}
            </div>
          )}
        </div>
      </div>
    </BackofficeLayout>
  )
}
